// The daily notification, from this browser's side.
//
// Everything impure about the setting is here - the service worker, the
// permission prompt, the browser's own subscription object, and the three calls
// in `api/client.ts` - so `NotificationSetting.tsx` is a screen and
// `push-support.ts` stays pure and testable.
//
// TWO RULES SHAPE ALL OF IT, and both are the kind that are easy to get wrong
// once and impossible to fix afterwards:
//
//   1. THE PERMISSION IS ASKED ONCE, EVER, AND ONLY AFTER SOMEBODY TURNS THE
//      SETTING ON. A browser remembers a dismissal and will not ask again - the
//      person has to find the site settings themselves - so a prompt fired on
//      page load spends the single ask this product gets, on that device, for
//      ever, on somebody who had not asked for anything. Nothing in this file
//      calls `requestPermission` outside `enable()`, and `enable()` is only ever
//      reached from a press. `web/test/notifications.test.tsx` asserts the load
//      path never touches it.
//
//   2. NOTHING IS OFFERED THAT CANNOT FIRE. If the deployment has no VAPID keys,
//      or the browser has no Push API, or the permission has already been
//      denied, there is no toggle - there is a sentence saying which of those it
//      is. A toggle that lies is worse than no toggle, and on iOS it is the
//      difference between a dead switch and a person adding the app to their
//      Home Screen and having it work.
//
// A SUBSCRIPTION BELONGS TO THIS BROWSER, so the setting does too - the same
// per-device shape the theme and the wording already take
// (`shell/preference.ts`). The server has to hold the endpoint and the time in
// order to send anything at all, so unlike those two the setting lives in the
// database, keyed by the browser's own endpoint rather than by the account. Its
// last saved endpoint is mirrored locally so the server row can still be found
// and turned off if the browser later evicts its subscription or Push API.

import { useCallback, useEffect, useRef, useState } from 'react';
import { api, isSignedOut, messageFor } from '../api/client.ts';
import type { PushSettingResponse } from '../api/client.ts';
import { clearPreference, readPreference, writePreference } from '../shell/preference.ts';
import {
  DEFAULT_SEND_AT_MINUTE,
  applicationServerKey,
  browserFacts,
  pushSupport,
  toBase64Url,
} from './push-support.ts';
import type { PushSupport } from './push-support.ts';

/** Where the service worker is served from. The root, and it has to be. */
const WORKER_URL = '/sw.js';
export const PUSH_ENDPOINT_KEY = 'pn_push_endpoint';

export interface DailyNotificationState {
  /** Null until the first read has answered. */
  setting: PushSettingResponse | null;
  support: PushSupport;
  /**
   * This browser has already refused notifications for this site. Read from
   * `Notification.permission` WITHOUT prompting, which is the only thing that
   * may be read before a press.
   */
  blocked: boolean;
  /** On exactly when this browser has a row on the server. Off is the default. */
  on: boolean;
  /** The minute of the local day it fires at, or would fire at. */
  minute: number;
  busy: boolean;
  problem: string | null;
  enable: () => void;
  disable: () => void;
  chooseMinute: (minute: number) => void;
}

interface Options {
  /**
   * The Settings screen is mounted and unmounted as a screen rather than always
   * present, so the read is gated exactly as the board's and the month's are -
   * nobody who never opens Settings pays a request for a feature they have not
   * heard of.
   */
  active: boolean;
  onSignedOut: () => void;
}

/**
 * The browser's own subscription for this site, or null.
 *
 * `getRegistration` RATHER THAN `register`, and that is the point: reading the
 * current state must not install a service worker on the machine of somebody who
 * has never asked for a notification. Registering happens in `enable()` and
 * nowhere else.
 */
async function currentSubscription(): Promise<PushSubscription | null> {
  const registration = await navigator.serviceWorker.getRegistration();
  if (registration === undefined) return null;
  return registration.pushManager.getSubscription();
}

function rememberedEndpoint(): string | undefined {
  const endpoint = readPreference(PUSH_ENDPOINT_KEY);
  if (endpoint === null) return undefined;
  try {
    return new URL(endpoint).protocol === 'https:' ? endpoint : undefined;
  } catch {
    return undefined;
  }
}

/** Whether an existing subscription was made with the key the server holds now. */
function madeWith(subscription: PushSubscription, publicKey: string): boolean {
  const applied = subscription.options.applicationServerKey;
  // Not every engine exposes it. Unknown counts as a match: re-subscribing on a
  // guess would throw away a working subscription, and a genuinely stale one
  // fails loudly at the server instead (403), which is recoverable.
  if (applied == null) return true;
  return toBase64Url(applied as ArrayBuffer) === publicKey;
}

export function useDailyNotification(options: Options): DailyNotificationState {
  const { active, onSignedOut } = options;

  const [support] = useState<PushSupport>(() =>
    typeof window === 'undefined'
      ? { kind: 'unsupported' }
      : pushSupport(browserFacts(navigator, window)),
  );
  const [setting, setSetting] = useState<PushSettingResponse | null>(null);
  const [minute, setMinute] = useState(DEFAULT_SEND_AT_MINUTE);
  const [blocked, setBlocked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  // The effect depends on WHICH read is wanted, not on how the caller happens to
  // be rendered: `onSignedOut` is a fresh closure every render of the shell, and
  // depending on it would re-read this setting on every keystroke anywhere in
  // the app. The same shape `useSchedules` in `shell/SettingsScreen.tsx` takes.
  const latest = useRef(onSignedOut);
  latest.current = onSignedOut;

  useEffect(() => {
    if (!active) return;
    let live = true;

    void (async () => {
      try {
        // READ, NEVER PROMPT. `Notification.permission` is a property; asking
        // for it shows nothing and costs nothing, which is what makes it the one
        // thing this effect may look at.
        if (support.kind === 'ready') setBlocked(Notification.permission === 'denied');

        // The endpoint is this browser's own, so the server can answer with THIS
        // browser's setting rather than with the account's. A browser that has
        // never subscribed sends none and gets `subscription: null`, which is
        // off - and still learns whether the deployment has the feature at all.
        const existing = support.kind === 'ready' ? await currentSubscription() : null;
        const remembered = rememberedEndpoint();
        let value = await api.pushSetting(remembered ?? existing?.endpoint);
        if (value.subscription === null && existing !== null && existing.endpoint !== remembered) {
          value = await api.pushSetting(existing.endpoint);
        }
        if (!live) return;
        setSetting(value);
        if (value.subscription !== null) {
          setMinute(value.subscription.sendAtMinute);
          writePreference(PUSH_ENDPOINT_KEY, value.subscription.endpoint, { secure: true });
        }
        setProblem(null);
      } catch (error) {
        if (isSignedOut(error)) {
          latest.current();
          return;
        }
        if (live) setProblem(messageFor(error));
      }
    })();

    return () => {
      live = false;
    };
  }, [active, support.kind]);

  const fail = useCallback(
    (error: unknown) => {
      if (isSignedOut(error)) {
        onSignedOut();
        return;
      }
      setProblem(messageFor(error));
    },
    [onSignedOut],
  );

  /**
   * Turn it on. THE ONLY PLACE IN THIS CLIENT THAT ASKS FOR PERMISSION.
   *
   * The permission is requested FIRST, before the service worker is registered,
   * and that order is load-bearing on Safari: the request has to come out of the
   * user gesture that started it, and awaiting a registration first has been
   * enough to lose the gesture and have the call rejected without a prompt.
   */
  const enable = useCallback(() => {
    const publicKey = setting?.publicKey;
    if (publicKey == null || support.kind !== 'ready') return;

    setBusy(true);
    setProblem(null);
    void (async () => {
      try {
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') {
          // Dismissed ('default') or refused ('denied'). Only the second is
          // permanent, and only the second turns the toggle into a sentence -
          // somebody who pressed Escape may press the button again.
          setBlocked(permission === 'denied');
          return;
        }

        await navigator.serviceWorker.register(WORKER_URL);
        // `ready` rather than the registration `register` returned: a worker has
        // to be ACTIVE before it can hold a push subscription, and a freshly
        // registered one is installing.
        const registration = await navigator.serviceWorker.ready;

        let subscription = await registration.pushManager.getSubscription();
        let replacesEndpoint: string | undefined;
        if (
          subscription !== null &&
          (setting?.subscription === null || !madeWith(subscription, publicKey))
        ) {
          replacesEndpoint = subscription.endpoint;
          await subscription.unsubscribe();
          subscription = null;
        }
        subscription ??= await registration.pushManager.subscribe({
          // Required, and it is a promise rather than a flag: every push WILL
          // result in a visible notification. A browser revokes the permission
          // of a site that pushes silently.
          userVisibleOnly: true,
          applicationServerKey: applicationServerKey(publicKey),
        });

        const saved = await api.subscribePush({
          endpoint: subscription.endpoint,
          p256dh: toBase64Url(subscription.getKey('p256dh')),
          auth: toBase64Url(subscription.getKey('auth')),
          sendAtMinute: minute,
          ...(replacesEndpoint === undefined ? {} : { replacesEndpoint }),
        });
        writePreference(PUSH_ENDPOINT_KEY, saved.endpoint, { secure: true });
        setSetting((was) => (was === null ? was : { ...was, subscription: saved }));
        setMinute(saved.sendAtMinute);
      } catch (error) {
        fail(error);
      } finally {
        setBusy(false);
      }
    })();
  }, [fail, minute, setting?.publicKey, setting?.subscription, support.kind]);

  /**
   * Turn it off.
   *
   * THE SERVER IS TOLD FIRST, and then the browser's own subscription is
   * dropped. The other order leaves a window in which the row still exists and
   * the endpoint it names is dead - which is a notification that cannot arrive
   * and a row that is only cleaned up the next time one is attempted. This way a
   * failure of the second half costs nothing: the server has already stopped.
   */
  const disable = useCallback(() => {
    const savedEndpoint = setting?.subscription?.endpoint;
    setBusy(true);
    setProblem(null);
    void (async () => {
      try {
        const subscription = support.kind === 'ready' ? await currentSubscription() : null;
        const endpoints = [
          ...new Set([savedEndpoint, rememberedEndpoint(), subscription?.endpoint]),
        ].filter((endpoint): endpoint is string => endpoint !== undefined);
        for (const endpoint of endpoints) await api.unsubscribePush(endpoint);
        clearPreference(PUSH_ENDPOINT_KEY, { secure: true });
        setSetting((was) => (was === null ? was : { ...was, subscription: null }));

        if (subscription !== null) {
          try {
            await subscription.unsubscribe();
          } catch (error) {
            fail(error);
          }
        }
      } catch (error) {
        fail(error);
      } finally {
        setBusy(false);
      }
    })();
  }, [fail, setting?.subscription?.endpoint, support.kind]);

  /**
   * Move the time.
   *
   * While it is OFF this only remembers the choice - there is no row to move, and
   * writing one would be turning the notification on by touching a time field.
   * While it is ON it is the same idempotent `PUT` that turned it on, with the
   * browser's own keys re-read rather than remembered: a browser may have
   * re-subscribed since, and sending stale keys would store an endpoint that
   * cannot be decrypted.
   */
  const chooseMinute = useCallback(
    (next: number) => {
      setMinute(next);
      const currentSetting = setting?.subscription;
      if (currentSetting == null || support.kind !== 'ready') return;

      setBusy(true);
      setProblem(null);
      void (async () => {
        try {
          const subscription = await currentSubscription();
          if (subscription === null) {
            // The browser dropped its subscription behind our back - an eviction,
            // or site data cleared in another tab. Say so rather than writing a
            // time against an endpoint that no longer exists.
            setMinute(currentSetting.sendAtMinute);
            setProblem(
              'This browser no longer has a push subscription. Turn the notification off, then on again.',
            );
            return;
          }
          const saved = await api.subscribePush({
            endpoint: subscription.endpoint,
            p256dh: toBase64Url(subscription.getKey('p256dh')),
            auth: toBase64Url(subscription.getKey('auth')),
            sendAtMinute: next,
            ...(currentSetting.endpoint === subscription.endpoint
              ? {}
              : { replacesEndpoint: currentSetting.endpoint }),
          });
          writePreference(PUSH_ENDPOINT_KEY, saved.endpoint, { secure: true });
          setSetting((was) => (was === null ? was : { ...was, subscription: saved }));
        } catch (error) {
          setMinute(currentSetting.sendAtMinute);
          fail(error);
        } finally {
          setBusy(false);
        }
      })();
    },
    [fail, setting?.subscription, support.kind],
  );

  return {
    setting,
    support,
    blocked,
    on: setting?.subscription != null,
    minute,
    busy,
    problem,
    enable,
    disable,
    chooseMinute,
  };
}
